/** URL of the minimal block-only service worker (no caching). */
export const BLOCK_ONLY_SW_PATH = "/service-worker-block-only.js";

/** Legacy PWA workers that must be removed — they blocked Turnstile PAT probes with synthetic 401. */
export const LEGACY_SERVICE_WORKER_MARKERS = [
  "/service-worker.js",
  "/service-worker-cdn.js",
] as const;

function workerUrl(registration: ServiceWorkerRegistration): string | undefined {
  return (
    registration.active?.scriptURL ??
    registration.waiting?.scriptURL ??
    registration.installing?.scriptURL
  );
}

export function registrationUsesBlockOnlyWorker(registration: ServiceWorkerRegistration): boolean {
  return [registration.active, registration.waiting, registration.installing].some((worker) =>
    worker?.scriptURL.includes("service-worker-block-only.js")
  );
}

/**
 * Is Legacy Service Worker Registration.
 */
export function isLegacyServiceWorkerRegistration(
  registration: ServiceWorkerRegistration
): boolean {
  const url = workerUrl(registration);
  if (!url) {
    return false;
  }
  if (url.includes("service-worker-block-only.js")) {
    return false;
  }
  return LEGACY_SERVICE_WORKER_MARKERS.some((marker) => url.includes(marker));
}

/**
 * Unregister legacy PWA service workers, then register the Turnstile-safe block-only worker once.
 */
export async function ensureBlockOnlyServiceWorker(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();

  await Promise.all(
    registrations
      .filter(isLegacyServiceWorkerRegistration)
      .map((registration) => registration.unregister())
  );

  const activeRegistrations = await navigator.serviceWorker.getRegistrations();
  if (activeRegistrations.some(registrationUsesBlockOnlyWorker)) {
    return;
  }

  await navigator.serviceWorker.register(BLOCK_ONLY_SW_PATH, { scope: "/" });
}
